import { useState, useCallback } from "react";
import { useDemoRequest } from "./DemoRequestContext";

interface FormData {
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  productInterest: string;
  businessNeed: string;
}

const initialForm: FormData = {
  fullName: "",
  companyName: "",
  email: "",
  phone: "",
  productInterest: "",
  businessNeed: "",
};

export default function DemoRequestModal() {
  const { isOpen, close } = useDemoRequest();
  const [formData, setFormData] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleChange = useCallback((field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    const next: Partial<Record<keyof FormData, string>> = {};
    if (!formData.fullName.trim()) next.fullName = "Full name is required";
    if (!formData.companyName.trim()) next.companyName = "Company name is required";
    if (!formData.email.trim()) {
      next.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) next.phone = "Phone number is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;
      setStatus("submitting");
      try {
        const data = new URLSearchParams();
        data.append("fullName", formData.fullName);
        data.append("companyName", formData.companyName);
        data.append("email", formData.email);
        data.append("phone", formData.phone);
        data.append("productInterest", formData.productInterest);
        data.append("businessNeed", formData.businessNeed);

        const res = await fetch("https://readdy.ai/api/form/d7ri64ul0bai2p3haofg", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: data.toString(),
        });

        if (res.ok) {
          setStatus("success");
          setFormData(initialForm);
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    },
    [formData, validate]
  );

  const handleClose = useCallback(() => {
    close();
    setTimeout(() => {
      setStatus("idle");
      setFormData(initialForm);
      setErrors({});
    }, 300);
  }, [close]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      <div className="relative w-full max-w-lg bg-graphite-900 border border-graphite-700/50 rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-graphite-700/50">
          <div>
            <h3 className="text-lg font-bold text-white">Book a Demo</h3>
            <p className="text-xs text-silver-400 mt-0.5">
              Our team will reach out to schedule your personalised walkthrough.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-silver-400 hover:text-white hover:bg-graphite-700/50 transition-colors cursor-pointer shrink-0"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 scrollbar-hide">
          {status === "success" ? (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-accent/10 border border-emerald-accent/20 mb-4">
                <i className="ri-checkbox-circle-line text-2xl text-emerald-accent" />
              </div>
              <h4 className="text-base font-bold text-white mb-2">Thank You</h4>
              <p className="text-sm text-silver-300 leading-relaxed max-w-xs">
                We have received your request and will be in touch shortly to schedule your demo.
              </p>
              <button
                onClick={handleClose}
                className="btn-primary mt-6 w-full max-w-xs"
              >
                Done
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="book-demo-form"
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-medium text-silver-300 mb-1">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                  placeholder="John Doe"
                />
                {errors.fullName && (
                  <p className="text-xs text-red-400 mt-1">{errors.fullName}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-silver-300 mb-1">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                  placeholder="Acme Inc."
                />
                {errors.companyName && (
                  <p className="text-xs text-red-400 mt-1">{errors.companyName}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                    placeholder="info@stackmapsystems.com"
                  />
                  {errors.email && (
                    <p className="text-xs text-red-400 mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-silver-300 mb-1">
                    Phone <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                    placeholder="+234 8163 000 0009"
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-400 mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-silver-300 mb-1">
                  Product Interest <span className="text-silver-500">(optional)</span>
                </label>
                <input
                  type="text"
                  name="productInterest"
                  value={formData.productInterest}
                  onChange={(e) => handleChange("productInterest", e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                  placeholder="e.g. WorkTrack, HCM, OMS"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-silver-300 mb-1">
                  Business Need <span className="text-silver-500">(optional)</span>
                </label>
                <textarea
                  name="businessNeed"
                  value={formData.businessNeed}
                  onChange={(e) => handleChange("businessNeed", e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full px-3.5 py-2.5 bg-graphite-800/80 border border-graphite-700/60 rounded-lg text-sm text-white placeholder-silver-500 focus:outline-none focus:border-emerald-accent/50 transition-colors resize-none"
                  placeholder="Tell us more about your specific needs..."
                />
                <p className="text-[10px] text-silver-500 text-right mt-1">
                  {formData.businessNeed.length}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="btn-primary w-full mt-2 disabled:opacity-60"
              >
                {status === "submitting" ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  "Request Demo"
                )}
              </button>
              {status === "error" && (
                <p className="text-xs text-red-400 text-center">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}